import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';

export type Theme = 'light' | 'dark';
export type ThemeSource = 'url' | 'host' | 'user' | 'system';

export interface ThemeContextValue {
    theme: Theme;
    source: ThemeSource;
    setTheme: (theme: Theme, source?: ThemeSource) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const LAST_HOST_THEME_KEY = '7punches:lastHostTheme';

declare global {
    interface Window {
        __7punchesInitialTheme?: { theme: Theme; source: ThemeSource };
    }
}

function readUrlParam(): Theme | null {
    try {
        const raw = new URLSearchParams(window.location.search)
            .get('theme')
            ?.toLowerCase();
        if (raw === 'dark' || raw === 'light') return raw;
    } catch {
        /* ignore */
    }
    return null;
}

function readSystemPreference(): Theme {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
}

function applyTheme(theme: Theme) {
    document.body.classList.toggle('theme-dark', theme === 'dark');
}

function resolveInitialState(): { theme: Theme; source: ThemeSource } {
    if (window.__7punchesInitialTheme) {
        return window.__7punchesInitialTheme;
    }

    const urlTheme = readUrlParam();
    if (urlTheme) return { theme: urlTheme, source: 'url' };

    const persisted = localStorage.getItem(LAST_HOST_THEME_KEY);
    if (persisted === 'dark' || persisted === 'light') {
        return { theme: persisted, source: 'host' };
    }

    return { theme: readSystemPreference(), source: 'system' };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const initial = resolveInitialState();
    const [theme, setThemeState] = useState<Theme>(initial.theme);
    const [source, setSource] = useState<ThemeSource>(initial.source);

    const sourceRef = useRef(source);
    sourceRef.current = source;

    const setTheme = useCallback(
        (next: Theme, nextSource: ThemeSource = 'user') => {
            const currentSource = sourceRef.current;

            // url source blocks host and system overrides, but the user can still
            // manually change the theme via the settings toggle
            if (currentSource === 'url' && nextSource !== 'user') return;

            // system is the lowest priority — blocked when any other source owns the theme
            if (nextSource === 'system' && currentSource !== 'system') return;

            setThemeState(next);
            setSource(nextSource);

            if (nextSource === 'host') {
                try {
                    localStorage.setItem(LAST_HOST_THEME_KEY, next);
                } catch {
                    /* ignore storage errors */
                }
            }
        },
        []
    );

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    // Sync system preference when source is 'system'
    useEffect(() => {
        if (source !== 'system') return;
        if (!window.matchMedia) return;

        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        function handleChange(e: MediaQueryListEvent) {
            if (sourceRef.current === 'system') {
                const next: Theme = e.matches ? 'dark' : 'light';
                setThemeState(next);
                applyTheme(next);
            }
        }

        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, [source]);

    const value = useMemo<ThemeContextValue>(
        () => ({ theme, source, setTheme }),
        [theme, source, setTheme]
    );

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return ctx;
}
